import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import { ClassNameType } from 'interfaces';
import ButtonType from './type';

export interface ButtonStyles {
  primary(disabled: boolean): string,

  secondary(disabled: boolean): string,

  pill(active: boolean): string,

  outline(active: boolean): string,

  pager(disabled: boolean, active: boolean): string,
  icon: string,
  legend: string,
  iconContainer(hasIcon: boolean): string,

  component(
    type: ButtonType,
    className: ClassNameType,
    disabled: boolean,
    active: boolean,
  ): string,
}

const styles: ButtonStyles = {
  primary: (disabled: boolean = false) => classNames(
    'rounded-3xl',
    '!px-6',
    '!py-1.5',
    'bg-secondaryBlue',
    'text-white',
    'shadow-md',
    '!border-secondaryBlue',
    'disabled:bg-paleGray-300',
    'disabled:text-paleGray-500',
    'disabled:!border-paleGray-400',
    'disabled:shadow-none',
    {
      'hover:bg-secondaryBlue-600': !disabled,
      'hover:text-white': !disabled,
    },
  ),
  secondary: (disabled: boolean = false) => classNames(
    'rounded-3xl',
    '!px-8',
    '!py-1.5',
    'text-mainBlue',
    '!border-mainBlue',
    'disabled:text-paleGray',
    'disabled:!border-paleGray',
    {
      'hover:bg-mainBlue': !disabled,
      'hover:text-white': !disabled,
    },
  ),
  pill: (active: boolean = false) => classNames(
    'rounded-3xl',
    '!px-4',
    '!py-1',
    'hover:bg-secondaryBlue',
    'hover:text-white',
    {
      'text-white bg-secondaryBlue': active,
      'text-tertiaryBlue bg-lightBlue': !active,
    },
  ),
  outline: (active: boolean = false) => classNames(
    'text-sky-800',
    '!py-5',
    'rounded-xl',
    '!border',
    'hover:!border-sky-300',
    {
      'ring-2 ring-sky-300': active,
      '!border-paleGray': !active,
    },
  ),
  pager: (disabled: boolean, active: boolean = false) => classNames(
    '!px-4',
    '!py-1.5',
    '!border-transparent',
    'disabled:text-darkGray',
    'text-sm',
    {
      'bg-secondaryBlue text-white': active,
      'bg-lightGray text-mainBlue': !active,
      'hover:bg-secondaryBlue hover:text-white': !disabled,
    },
  ),
  component: (
    type: ButtonType,
    className: ClassNameType,
    disabled: boolean = false,
    active: boolean = false,
  ) => {
    const primaryButtonClasses = styles.primary(disabled);
    const secondaryButtonClasses = styles.secondary(disabled);
    const pillButtonClasses = styles.pill(active);
    const outlineButtonClasses = styles.outline(active);
    const pagerButtonClasses = styles.pager(disabled, active);
    const defaultButtonsClasses = classNames(
      'font-bold',
      '!border',
      'text-lg',
    );

    const isDefaultButton = type !== ButtonType.PILL;

    return twMerge(classNames(
      'transition-all',
      {
        [defaultButtonsClasses]: isDefaultButton,
        [primaryButtonClasses]: type === ButtonType.PRIMARY,
        [primaryButtonClasses]: type === ButtonType.PRIMARY,
        [secondaryButtonClasses]: type === ButtonType.SECONDARY,
        [pillButtonClasses]: type === ButtonType.PILL,
        [outlineButtonClasses]: type === ButtonType.OUTLINE,
        [pagerButtonClasses]: type === ButtonType.PAGER,
      },
    ), className);
  },
  icon: classNames('!mr-4'),
  legend: classNames(
    'text-xs',
    'text-darkGray',
    'font-thin',
    '!m-0',
  ),
  iconContainer: (hasIcon: boolean) => classNames(
    {
      'flex justify-center': hasIcon,
    },
  ),
};

export default styles;
